/**
 * Storage utilities for managing the review reminder modal dismissal state.
 * Uses localStorage to persist dismissal state per user with daily reset.
 */

const STORAGE_KEY_PREFIX = 'review-reminder-dismissed-';

/**
 * Get the storage key for a specific user
 */
function getStorageKey(userId: string): string {
    return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Get today's date as a string in YYYY-MM-DD format
 */
function getTodayDateString(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
}

/**
 * Check if the review reminder should be shown for a user.
 * Returns true if the reminder was not dismissed today.
 * @param userId - The user's ID
 */
export function shouldShowReminder(userId: string): boolean {
    if (!userId) return false;

    try {
        const storageKey = getStorageKey(userId);
        const dismissedDate = localStorage.getItem(storageKey);

        if (!dismissedDate) {
            return true; // Never dismissed, show reminder
        }

        const today = getTodayDateString();
        return dismissedDate !== today; // Show if dismissed on a different day
    } catch {
        // localStorage might not be available
        return true;
    }
}

/**
 * Dismiss the review reminder for today.
 * Stores today's date in localStorage to prevent showing again until tomorrow.
 * @param userId - The user's ID
 */
export function dismissReminder(userId: string): void {
    if (!userId) return;

    try {
        const storageKey = getStorageKey(userId);
        const today = getTodayDateString();
        localStorage.setItem(storageKey, today);
    } catch {
        // localStorage might not be available
        console.warn('Failed to save reminder dismissal state');
    }
}

/**
 * Clear the dismissal state for a user (useful for testing).
 * @param userId - The user's ID
 */
export function clearDismissal(userId: string): void {
    if (!userId) return;

    try {
        const storageKey = getStorageKey(userId);
        localStorage.removeItem(storageKey);
    } catch {
        // localStorage might not be available
        console.warn('Failed to clear reminder dismissal state');
    }
}
